import {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react';
import {assert} from 'assert-ts';
import {Concept, type EntityMainType, type EntitySubType} from 'types';
import {HTTP_STATUS_ABORTED, HttpError} from 'api/http/types';
import type {DataLoadStatus} from 'api/types';
import {
  useLazySearchAgentsCountQuery,
  useLazySearchSeriesCountQuery,
} from 'services/elastic/elasticApi';
import type {SearchMainEntityType} from '../types';
import {trimSearchQuery} from '../functions';

export type SearchCountResult = Partial<Record<EntitySubType, number>>;

export const EMPTY_COUNT_RESULT: SearchCountResult = {};
export const DEFAULT_COUNT_RESULT: DataLoadStatus<SearchCountResult> = {
  status: 'Loaded',
  data: EMPTY_COUNT_RESULT,
};

type SearchEntityCountFunc = (
  search: string,
  signal: AbortSignal,
) => Promise<SearchCountResult>;

export const useEntitySearchCount = (
  searchQuery: string,
  entityType: SearchMainEntityType,
): DataLoadStatus<SearchCountResult> => {
  const [result, setResult] =
    useState<DataLoadStatus<SearchCountResult>>(DEFAULT_COUNT_RESULT);
  const onSetResult = useEffectEvent(setResult);
  const searchQueryRef = useRef<string | undefined>(undefined);
  const entityTypeRef = useRef<EntityMainType>(entityType);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const [triggerAgentsCountSearch] = useLazySearchAgentsCountQuery();
  const [triggerSeriesCountSearch] = useLazySearchSeriesCountQuery();

  const runSearch = useCallback(
    (type: SearchMainEntityType): SearchEntityCountFunc | undefined => {
      switch (type) {
        case Concept.agent:
          return search => triggerAgentsCountSearch({query: search}).unwrap();
        case Concept.collection:
          return search => triggerSeriesCountSearch({query: search}).unwrap();
        case Concept.work:
        case Concept.prize:
          // No subtypes
          return () => Promise.resolve({});
        default:
          return undefined;
      }
    },
    [triggerAgentsCountSearch, triggerSeriesCountSearch],
  );

  // When search query or entity type/subtype is changed
  // => abort any ongoing search, search with new criteria
  useEffect(() => {
    if (
      searchQuery === searchQueryRef.current &&
      entityType === entityTypeRef.current
    ) {
      return;
    }

    // Abort any ongoing call
    if (abortControllerRef.current) {
      // Should call rtkQuery.cancel() instead. This abort does not work anymore.
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }

    if (!searchQuery) {
      onSetResult(DEFAULT_COUNT_RESULT);
      return;
    }

    if (entityType === entityTypeRef.current) {
      onSetResult(prev => ({
        ...prev,
        status: 'Loading',
      }));
    }

    abortControllerRef.current = new AbortController();
    searchQueryRef.current = searchQuery;
    entityTypeRef.current = entityType;

    const trimmedQuery = trimSearchQuery(searchQuery);
    const search = assert(
      runSearch(entityType),
      'runSearch[entityType] expected',
      {entityType},
    );

    search(trimmedQuery, abortControllerRef.current.signal)
      .then(data => {
        onSetResult({status: 'Loaded', data});
      })
      .catch(error => {
        if (
          error instanceof HttpError &&
          error.status === HTTP_STATUS_ABORTED
        ) {
          // Caused by abort above, leave state as is - to be updated by next call
          return;
        }

        console.error('useEntitySearchCount: search failed', error);
        onSetResult({
          status: 'Failed',
          error,
        });
      });
  }, [searchQuery, entityType, runSearch]);

  return result;
};
